// Left rail: brand header, workspace list, inline new-workspace form,
// two-stage armed-delete, version footer. A01 collapsed the pre-pivot
// project+channel nesting — workspaces are top-level, no channels.

import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { ChevronDown, ChevronRight, FolderGit2, Network } from "lucide-react";
import { BrandLogo } from "../layout/Brand";
import { ArmedDeleteButton } from "./ArmedDeleteButton";
import { FileTreePanel } from "./FileTreePanel";
import type { RepoConnection } from "./types";

export function RailTree({
  userWorkspaces,
  workspaceIdNum,

  armedDeleteWorkspaceId,
  renamingWorkspaceId,
  renameDraft,

  orgName,
  newWorkspace,
  activeRepoConnections,

  onNavigate,
  onDeleteWorkspace,
  onStartRenameWorkspace,
  onRenameDraftChange,
  onSubmitRenameWorkspace,
  onCancelRenameWorkspace,
  onSubmitCreateWorkspace,
  onClearError,

  appVersion,
  envLabel,
}: {
  userWorkspaces: Array<{
    workspace_id: number;
    org_id: number;
    slug: string;
    name: string;
  }>;
  workspaceIdNum: number;

  armedDeleteWorkspaceId: number | null;
  renamingWorkspaceId: number | null;
  renameDraft: string;

  orgName: string | null;
  newWorkspace: {
    open: boolean;
    name: string;
    busy: boolean;
    setOpen: (open: boolean | ((v: boolean) => boolean)) => void;
    setName: (name: string) => void;
  };
  activeRepoConnections: RepoConnection[];

  onNavigate: NavigateFunction;
  onDeleteWorkspace: (w: { workspace_id: number; name: string }) => void;
  onStartRenameWorkspace: (w: { workspace_id: number; name: string }) => void;
  onRenameDraftChange: (value: string) => void;
  onSubmitRenameWorkspace: (e: React.FormEvent) => void;
  onCancelRenameWorkspace: () => void;
  onSubmitCreateWorkspace: (e: React.FormEvent) => void;
  onClearError: () => void;

  appVersion: string;
  envLabel: string;
}) {
  function openRepoFile(repo: RepoConnection, path: string) {
    const params = new URLSearchParams();
    params.set("tab", "files");
    params.set("repo", repo.github_repo);
    params.set("file", path);
    onNavigate(`/workspaces/${workspaceIdNum}?${params.toString()}`);
  }

  return (
    <aside className="relative flex min-h-0 min-w-0 flex-col overflow-hidden bg-transparent">
      {/* Org / workspace name in the top 56-px header slot. Kept
          in lockstep with the SidebarShell version so every page
          wears the same chrome. break-all lets long emails wrap
          mid-token (line-clamp-2 alone won't break "captain+id@…"
          because there's no space). Subtle bottom border separates
          the header from the workspace list below. */}
      <div className="relative flex h-14 min-w-0 flex-col items-center justify-center border-b border-edge px-2.5">
        <h2
          className="line-clamp-2 text-center text-sm font-semibold leading-tight break-all text-primary"
          title={orgName ?? "Workspaces"}
          data-testid="sidebar-org"
        >
          {orgName ?? "Workspaces"}
        </h2>
      </div>
      <ul
        className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3"
        data-testid="workspaces-list"
      >
        <li className="mb-1 border-b border-edge pb-1">
          <button
            type="button"
            onClick={() => onNavigate("/orchestrator")}
            className="flex w-full cursor-pointer items-center gap-1.5 px-1.5 py-1 text-left text-[0.8125rem] font-medium text-primary transition-colors hover:text-accent"
            title="Orchestrator"
            data-testid="sidebar-orchestrator"
          >
            <Network size={12} className="shrink-0 text-muted" />
            <span className="truncate">orchestrator</span>
          </button>
        </li>
        {userWorkspaces.map((w) => {
          const isCurrent = w.workspace_id === workspaceIdNum;
          const armed = armedDeleteWorkspaceId === w.workspace_id;
          const renaming = renamingWorkspaceId === w.workspace_id;
          return (
            <li key={w.workspace_id} className="flex flex-col">
              <div
                className="group relative flex items-center"
                data-testid={`workspace-row-${w.slug}`}
              >
                {renaming ? (
                  <form
                    onSubmit={onSubmitRenameWorkspace}
                    className="flex-1"
                    data-testid={`workspace-${w.slug}-rename-form`}
                  >
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => onRenameDraftChange(e.target.value)}
                      onBlur={(e) => {
                        // Submit on blur unless empty → cancel.
                        if (renameDraft.trim() === "") {
                          onCancelRenameWorkspace();
                        } else {
                          onSubmitRenameWorkspace(
                            e as unknown as React.FormEvent,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          onCancelRenameWorkspace();
                        }
                      }}
                      maxLength={80}
                      className="w-full rounded-sm border border-accent bg-surface-glass px-1.5 py-0.5 text-[0.8125rem] font-medium text-primary outline-none"
                      data-testid={`workspace-${w.slug}-rename-input`}
                    />
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => onNavigate(`/workspaces/${w.workspace_id}`)}
                    onDoubleClick={() =>
                      onStartRenameWorkspace({
                        workspace_id: w.workspace_id,
                        name: w.name,
                      })
                    }
                    className={`flex-1 cursor-pointer truncate px-1.5 py-0.5 text-left text-[0.8125rem] font-medium transition-colors ${
                      isCurrent
                        ? "text-accent"
                        : "text-primary hover:text-accent"
                    }`}
                    data-testid={`workspace-${w.slug}`}
                    title={`${w.name} (double-click to rename)`}
                  >
                    {w.name}
                  </button>
                )}
                {!isCurrent && !renaming && (
                  <ArmedDeleteButton
                    armed={armed}
                    onDelete={() =>
                      onDeleteWorkspace({
                        workspace_id: w.workspace_id,
                        name: w.name,
                      })
                    }
                    size={12}
                    label={`Delete ${w.name}`}
                    testId={`workspace-${w.slug}-delete`}
                    className="absolute right-1"
                  />
                )}
              </div>
              {isCurrent && activeRepoConnections.length > 0 && (
                <div
                  className="mt-0.5 ml-2 border-l border-edge pl-1.5"
                  data-testid="sidebar-repo-tree"
                >
                  <div className="mb-0.5 flex items-center gap-1 px-1 text-[0.625rem] font-medium text-faint">
                    <FolderGit2 size={10} className="shrink-0" />
                    <span className="truncate">Repos</span>
                  </div>
                  <ul className="flex flex-col gap-0.5">
                    {activeRepoConnections.map((repo) => (
                      <RailRepoNode
                        key={repo.repo_connection_id}
                        workspaceId={workspaceIdNum}
                        repo={repo}
                        onNavigate={onNavigate}
                        onOpenFile={(path) => openRepoFile(repo, path)}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
        {!newWorkspace.open ? (
          <li>
            <button
              type="button"
              onClick={() => {
                newWorkspace.setOpen(true);
                newWorkspace.setName("");
                onClearError();
              }}
              className="group/new-workspace flex w-full cursor-pointer items-center text-left text-faint transition-colors hover:text-accent"
              title="New workspace"
              aria-label="New workspace"
              data-testid="new-workspace-toggle"
            >
              <span className="flex h-6 w-5 shrink-0 items-center justify-center text-faint transition-colors group-hover/new-workspace:text-accent">
                <ChevronRight size={12} />
              </span>
              <span className="px-1.5 py-0.5 text-[0.8125rem]">create</span>
            </button>
          </li>
        ) : (
          <li>
            <form
              onSubmit={onSubmitCreateWorkspace}
              className="flex w-full min-w-0 items-center"
              data-testid="new-workspace-form"
            >
              <span className="flex h-6 w-5 shrink-0 items-center justify-center text-faint">
                <ChevronRight size={12} />
              </span>
              <input
                autoFocus
                value={newWorkspace.name}
                onChange={(e) => newWorkspace.setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    newWorkspace.setOpen(false);
                    newWorkspace.setName("");
                  }
                }}
                onBlur={() => {
                  if (newWorkspace.name.trim() === "") {
                    newWorkspace.setOpen(false);
                  }
                }}
                placeholder="Workspace name"
                maxLength={80}
                disabled={newWorkspace.busy}
                className="min-w-0 flex-1 bg-transparent px-1.5 py-0.5 text-[0.8125rem] font-medium text-primary outline-none placeholder:text-faint"
                data-testid="new-workspace-name"
              />
            </form>
          </li>
        )}
      </ul>
      {/* Bottom of rail: Waytide brand cluster sits ABOVE the version
          footer, centered horizontally in the rail. 2026-04-27 layout
          swap — moved here from the top of the rail so the org name
          owns the top slot and the brand quietly anchors the bottom. */}
      <div className="flex justify-center border-t border-edge pt-2 pb-1">
        <button
          onClick={() => onNavigate("/")}
          aria-label="Home"
          title="Home"
          className="flex cursor-pointer items-center gap-1.5 text-accent transition-colors hover:text-primary"
          data-testid="brand-home"
        >
          <BrandLogo size={16} />
          <span className="text-xs font-semibold leading-none text-primary">
            Waytide
          </span>
        </button>
      </div>
      <button
        onClick={() => onNavigate("/status")}
        title="Status"
        className="cursor-pointer py-2 text-center leading-none text-faint transition-colors hover:text-primary"
        style={{ fontSize: "0.5rem" }}
        data-testid="brand-version"
      >
        v{appVersion}
        {envLabel !== "production" ? ` · ${envLabel}` : ""}
      </button>
    </aside>
  );
}

function RailRepoNode({
  workspaceId,
  repo,
  onNavigate,
  onOpenFile,
}: {
  workspaceId: number;
  repo: RepoConnection;
  onNavigate: NavigateFunction;
  onOpenFile: (path: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li className="min-w-0" data-testid={`sidebar-repo-${repo.github_repo}`}>
      <div className="flex min-w-0 items-center gap-0.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex h-5 w-4 shrink-0 cursor-pointer items-center justify-center text-faint hover:text-accent"
          aria-label={`${expanded ? "Collapse" : "Expand"} ${repo.github_repo}`}
          title={`${expanded ? "Collapse" : "Expand"} ${repo.github_repo}`}
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button
          type="button"
          onClick={() =>
            onNavigate(
              `/workspaces/${workspaceId}?tab=files&repo=${encodeURIComponent(
                repo.github_repo,
              )}`,
            )
          }
          className="min-w-0 flex-1 cursor-pointer truncate py-0.5 text-left font-code text-[0.6875rem] text-muted hover:text-accent"
          title={`${repo.github_owner}/${repo.github_repo}`}
        >
          {repo.github_repo}
        </button>
      </div>
      {expanded && (
        <div className="ml-4 h-72 overflow-hidden rounded-sm border border-edge bg-surface-glass">
          <FileTreePanel
            workspaceId={workspaceId}
            repo={repo.github_repo}
            onOpenFile={onOpenFile}
          />
        </div>
      )}
    </li>
  );
}
